import { useCallback, useEffect, useRef, useState } from "react";
import { api, wsUrl } from "../lib/api";
import { relativeTime } from "../lib/format";

export type LiveEvent = {
  type: string;
  at?: string;
  roomId?: string;
  roomName?: string;
  viewId?: string;
  visitorEmail?: string;
  visitorName?: string;
  documentId?: string;
  documentName?: string;
  pageNumber?: number;
};

function describeEvent(ev: LiveEvent): string {
  const who = ev.visitorName || ev.visitorEmail || "Someone";
  if (ev.type === "session_start") return `${who} opened ${ev.roomName || "a Space"}`;
  if (ev.type === "page_view") {
    const doc = ev.documentName || "a document";
    if (ev.pageNumber) return `${who} is on page ${ev.pageNumber} of ${doc}`;
    return `${who} is viewing ${doc}`;
  }
  return "";
}

export function useLiveVisitFeed(path: string | null, enabled = true) {
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [connected, setConnected] = useState(false);
  /* Distinguishes the first paint ("Connecting") from a dropped socket
     ("Reconnecting") in status labels. */
  const [everConnected, setEverConnected] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<number | undefined>(undefined);
  const pingRef = useRef<number | undefined>(undefined);
  const seenRef = useRef<Set<string>>(new Set());

  const pushEvent = useCallback((raw: LiveEvent) => {
    const key = `${raw.type}:${raw.viewId}:${raw.documentId}:${raw.pageNumber}:${raw.at ?? ""}`;
    if (seenRef.current.has(key)) return;
    seenRef.current.add(key);
    if (seenRef.current.size > 400) {
      const arr = [...seenRef.current].slice(-200);
      seenRef.current = new Set(arr);
    }
    const ev = { ...raw, at: raw.at ?? new Date().toISOString() };
    const msg = describeEvent(ev);
    if (msg) {
      setBanner(msg);
      window.setTimeout(() => setBanner((b) => (b === msg ? null : b)), 8000);
    }
    setEvents((prev) => [ev, ...prev].slice(0, 50));
  }, []);

  useEffect(() => {
    if (!enabled || !path) return;

    let cancelled = false;

    api<{ events: LiveEvent[] }>(path)
      .then((res) => {
        if (cancelled) return;
        const history = (res.events ?? []).slice().reverse();
        for (const ev of history) pushEvent(ev);
      })
      .catch(() => undefined);

    const connect = () => {
      if (cancelled) return;
      const token = localStorage.getItem("adminSessionToken");
      const url = `${wsUrl(path)}${token ? `?token=${encodeURIComponent(token)}` : ""}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) return;
        setConnected(true);
        setEverConnected(true);
        pingRef.current = window.setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 25000);
      };

      ws.onmessage = (msg) => {
        if (cancelled || msg.data === "pong") return;
        try {
          pushEvent(JSON.parse(String(msg.data)) as LiveEvent);
        } catch {
          /* ignore */
        }
      };

      ws.onclose = () => {
        setConnected(false);
        window.clearInterval(pingRef.current);
        if (!cancelled) {
          reconnectRef.current = window.setTimeout(connect, 3000);
        }
      };

      ws.onerror = () => ws.close();
    };

    connect();

    return () => {
      cancelled = true;
      window.clearTimeout(reconnectRef.current);
      window.clearInterval(pingRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [path, enabled, pushEvent]);

  return { events, connected, everConnected, banner, formatTime: (at?: string) => (at ? relativeTime(at) : "now") };
}
